// A building's contacts as one list: emergency lines pinned at the top, then everyone else alphabetically, with the
// suppliers that cover every building marked as such. A row opens the contact; New and Link live in the page header.
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Mail, Phone } from 'lucide-react';
import { Button, Input, Modal, Select, type MenuEntry } from '../ui';
import { DataTable, type Column } from '../ui/DataTable';
import { FilterMenu, type FilterValue } from '../ui/FilterMenu';
import { FormError } from '../forms/common';
import { contactsApi, CONTACT_KINDS, telHref, type Contact } from '../../lib/building';
import { ContactDrawer } from './ContactDrawer';
import { formatPhone } from '../../lib/phone';

type Row = Contact & { here: string | null; shared: boolean };

export function PropertyContactsTab({ propertyId, onMenu }: { propertyId: string; /** Hands "New contact" and "Link existing" up to the page header. */ onMenu?: (m: MenuEntry[]) => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['property-contacts', propertyId], queryFn: () => contactsApi.forProperty(propertyId) });
  const [open, setOpen] = useState<string | 'new' | null>(null);
  const [linking, setLinking] = useState(false);
  const [filters, setFilters] = useState<FilterValue>({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const PER_PAGE = 10;
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['property-contacts', propertyId] }); void qc.invalidateQueries({ queryKey: ['who-to-call', propertyId] }); void qc.invalidateQueries({ queryKey: ['building-record', propertyId] }); };

  useEffect(() => {
    if (!onMenu) return;
    onMenu([
      { label: 'New contact', icon: 'Plus', can: 'contacts.create', onClick: () => setOpen('new') },
      { label: 'Link existing', icon: 'Link2', can: 'contacts.update', onClick: () => setLinking(true) },
    ]);
    return () => onMenu([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!onMenu]);

  // One list: this building's contacts (with their label here), then the ones covering every building.
  const rows = useMemo<Row[]>(() => {
    const linked = (q.data?.linked ?? []).map((c) => ({ ...c, here: c.properties.find((p) => p.propertyId === propertyId)?.label ?? null, shared: false }));
    const shared = (q.data?.shared ?? []).map((c) => ({ ...c, here: null, shared: true }));
    const all = [...linked, ...shared];
    const kinds = filters.kind ?? []; const onlyEmergency = (filters.flag ?? []).includes('emergency');
    const needle = search.trim().toLowerCase();
    const matches = (c: Row) => !needle || [c.name, c.company, c.role, c.here, c.email, c.phone, c.phoneAlt, formatPhone(c.phone).replace(/ /g, ''), formatPhone(c.phoneAlt).replace(/ /g, ''), c.kindLabel, ...c.trades].some((v) => v && v.toLowerCase().includes(needle));
    return all
      .filter((c) => matches(c) && (!kinds.length || kinds.includes(c.kind)) && (!onlyEmergency || c.isEmergency))
      .sort((a, b) => Number(b.isEmergency) - Number(a.isEmergency) || a.displayName.localeCompare(b.displayName));
  }, [q.data, propertyId, filters, search]);
  const pages = Math.max(1, Math.ceil(rows.length / PER_PAGE));
  const current = Math.min(page, pages);
  const pageRows = rows.slice((current - 1) * PER_PAGE, current * PER_PAGE);

  const columns: Column<Row>[] = [
    { key: 'displayName', label: 'Name', lockedVisible: true, render: (c) => (
      <div className="flex items-start gap-2">
        {c.isEmergency ? <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" /> : <span className="w-3.5 shrink-0" />}
        <div className="min-w-0">
          <div className="font-medium text-ink-900">{c.company && c.company !== c.name ? c.company : c.name}</div>
          <div className="truncate text-[11.5px] text-ink-500">{[c.company && c.company !== c.name ? c.name : null, c.kindLabel].filter(Boolean).join(' · ')}</div>
        </div>
      </div>
    ) },
    { key: 'role', label: 'Role', sortValue: (c) => c.here ?? c.role ?? c.trades[0] ?? '', render: (c) => (
      <div className="min-w-0">
        <div className="text-ink-800">{c.here ?? c.role ?? c.trades[0] ?? '—'}</div>
        {c.shared && <div className="text-[11px] text-ink-400">All buildings</div>}
      </div>
    ) },
    { key: 'phone', label: 'Phone', sortable: false, render: (c) => (c.phone || c.phoneAlt) ? <a href={telHref((c.phone ?? c.phoneAlt)!)} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1.5 whitespace-nowrap font-medium tabular-nums text-bronze-700 hover:underline"><Phone className="h-3 w-3" />{formatPhone(c.phone ?? c.phoneAlt)}</a> : <span className="text-ink-300">—</span> },
    { key: 'email', label: 'Email', sortable: false, hideOnMobile: true, render: (c) => c.email ? <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()} className="inline-flex max-w-[220px] items-center gap-1.5 truncate text-ink-700 hover:text-bronze-700 hover:underline"><Mail className="h-3 w-3 shrink-0" /><span className="truncate">{c.email}</span></a> : <span className="text-ink-300">—</span> },
  ];

  return (
    <div className="space-y-3">
      {q.error && <FormError error={q.error} />}
      <DataTable columns={columns} rows={pageRows} meta={{ page: current, perPage: PER_PAGE, total: rows.length, totalPages: pages }} page={current} onPage={setPage} isLoading={q.isLoading} rowKey={(c) => c.id} onRowClick={(c) => setOpen(c.id)}
        search={search} onSearch={(v) => { setSearch(v); setPage(1); }} searchPlaceholder="Name, role, phone…" columnPicker={{ storageKey: 'development.contacts.columns' }}
        emptyTitle={search || Object.values(filters).some((v) => v.length) ? 'No contacts match' : 'No contacts yet'} emptyBody="Add the out-of-hours line, the lift and fire contractors, and the utilities, and they appear on the property's Who to call list."
        filters={<FilterMenu value={filters} onChange={(v) => { setFilters(v); setPage(1); }} groups={[
          { key: 'flag', label: 'Emergency', single: true, options: [{ value: 'emergency', label: 'Emergency lines only' }] },
          { key: 'kind', label: 'Type', options: CONTACT_KINDS.map((k) => ({ value: k.key, label: k.label })) },
        ]} />} />
      {linking && <LinkExisting propertyId={propertyId} excluded={new Set(rows.map((c) => c.id))} onClose={() => { setLinking(false); refresh(); }} />}
      {open && <ContactDrawer id={open === 'new' ? null : open} defaultPropertyId={propertyId} onClose={() => { setOpen(null); refresh(); }} />}
    </div>
  );
}

/** Link someone already in the address book to this building, with what they are here ("Out of hours"). */
function LinkExisting({ propertyId, excluded, onClose }: { propertyId: string; excluded: Set<string>; onClose: () => void }) {
  const [search, setSearch] = useState('');
  const [pick, setPick] = useState('');
  const [label, setLabel] = useState('');
  const book = useQuery({ queryKey: ['/contacts', 'search', search], queryFn: async () => (await contactsApi.list({ q: search || undefined, perPage: 50 })).rows });
  const link = useMutation({ mutationFn: () => contactsApi.link(pick, propertyId, label || null), onSuccess: onClose });
  const choices = (book.data ?? []).filter((c) => !excluded.has(c.id));
  return (
    <Modal open onClose={onClose} title="Link an existing contact" footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={() => link.mutate()} disabled={!pick || link.isPending}>{link.isPending ? 'Linking…' : 'Link to this building'}</Button></>}>
      <div className="space-y-3">
        <Input className="w-full" placeholder="Search the address book…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Select className="w-full" value={pick} onChange={(e) => setPick(e.target.value)}>
          <option value="">Choose a contact…</option>
          {choices.map((c) => <option key={c.id} value={c.id}>{c.displayName} — {c.kindLabel}</option>)}
        </Select>
        <Input className="w-full" placeholder='What they are here, e.g. "Out of hours" (optional)' value={label} onChange={(e) => setLabel(e.target.value)} />
        <FormError error={link.error} />
      </div>
    </Modal>
  );
}
